
/**
 * Format a date to MMM DD, YYYY format
 * @param date The date to format
 * @returns Formatted date string (e.g., "May 15, 2023")
 */
export const formatDate = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };
  
  return date.toLocaleDateString('en-US', options);
};

/**
 * Generate a seed from a date for consistent random word selection
 * @param date The date to use as a seed
 * @returns A number to use as a seed
 */
export const getDateSeed = (date: Date): number => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  // Create a simple deterministic number from the date
  return (year * 10000) + (month * 100) + day;
};

/**
 * Format a date to YYYYMMDD format
 * @param date The date to format
 * @returns Formatted date string (e.g., "20230515")
 */
export const getDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

/**
 * Format a date to YYYY-MM-DD format
 * @param date The date to format
 * @returns Formatted date string (e.g., "2023-05-15")
 */
export const getDateString = (providedDate?: Date): string => {
  const date = providedDate ? providedDate : getNormalizedDate(); // If date is provided, use it, otherwise use the current date
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Convert a date key (YYYYMMDD) to a date string (YYYY-MM-DD)
 * @param dateKey The date key to convert (e.g., "20230515")
 * @returns Formatted date string (e.g., "2023-05-15")
 */
export const dateKeyToDateString = (dateKey: string): string => {
  const year = dateKey.substring(0, 4);
  const month = dateKey.substring(4, 6);
  const day = dateKey.substring(6, 8);
  return `${year}-${month}-${day}`;
};

/**
 * Convert a date string (YYYY-MM-DD) to a Date object
 * @param dateString The date string to convert (e.g., "2023-05-15")
 * @returns Date object
 */
export const dateFromDateString = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return getNormalizedDate(new Date(year, month - 1, day)); // month is 0-indexed in Date
};


/**
 * Get a normalized date without time for consistent day-to-day comparisons
 * @returns Normalized date with time set to midnight
 */
export const getNormalizedDate = (providedDate?: Date): Date => {
  const date = providedDate ? new Date(providedDate) : new Date(); // If date is provided, use it, otherwise use the current date
  const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return normalizedDate;
};

export const getNextDaysDate = (providedDate?: Date): Date => {
  const date = providedDate || new Date();
  const nextDay = new Date(date);
  nextDay.setDate(date.getDate() + 1);
  return nextDay
};

/**
 * Calculate the number of days since Fourdle started (June 19, 2021)
 * @returns The number representing the current Fourdle/Fourdle number
 */
export const getWordNumber = (date?: Date): number => {
  const fourdleStartDate = new Date(2021, 5, 19); // June 19, 2021 (months are 0-indexed)
  const today = getNormalizedDate(date);
  
  // Calculate the difference in milliseconds
  const diffTime = Math.abs(today.getTime() - fourdleStartDate.getTime());
  
  // Convert to days
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  // The number starts at 0
  return diffDays;
};

export function jsonDateReviver(key: string, value: any): any {
  // Regular expression to check if a string looks like an ISO date string
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
  // Regular expression to check if a string looks like a date string
  const dateStringRegex = /^\d{4}-\d{2}-\d{2}$/;

  if (typeof value === 'string') {
    let date: Date | null = null;

    if (isoDateRegex.test(value)) {
      date = getNormalizedDate(new Date(value));
    } else if (dateStringRegex.test(value)) {
      date = dateFromDateString(value);
    }

    if (!isNaN(date?.getTime())) { // Check if the parsed date is valid
      return date;
    }
  }

  return value; // Return other values unchanged
}